import React from 'react';
import { Stack } from '@mui/material';
import { CssTextField } from 'pages/reference-implementation/components/select';
import { useMutation } from 'react-query';
import axios, { endPoints } from 'api/request';
import { useRecoilState } from 'recoil';
import { OrderPayload, isBudgetCodeValid, budgetCodeValidationModal } from 'pages/reference-implementation/provisioning/RI002O/store';
import { InputLabel } from 'pages/reference-implementation/components/input-label';
import HelpImg from 'pages/provisioning/_components/helpImg';
import Btn from 'components/button';
import ProvisioningStyles from 'pages/provisioning/vm/style';
import { COST_CENTER_H_PREFIX_HELPER_TEXT, COST_CENTER_H_TOOLTIP_TEXT, COST_CENTER_VALIDATE_HELPER_TEXT } from 'utils/constant';

export const BudgetCode = () => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [budgetCodeValid, setBudgetCodeValid] = useRecoilState(isBudgetCodeValid);
    const [, setBudgetCodeValidation] = useRecoilState(budgetCodeValidationModal);
    const hPrefixError = /^[Hh]/.test(order?.budgetCode || '');
    const [apiValidationMessage, setApiValidationMessage] = React.useState('');

    const setBudgetCode = (budgetCode = null) => {
        setApiValidationMessage('');
        setBudgetCodeValid(false);
        setOrder({
            ...order,
            budgetCode,
            enabledManualBudgetCode: false,
        });
    };

    const handleBudgetCodeResult = (isSuccess) => {
        if (!isSuccess) {
            setBudgetCodeValid(false);
            setBudgetCodeValidation({ open: true, budgetCode: order?.budgetCode?.trim() });
        } else {
            setApiValidationMessage('');
            setBudgetCodeValid(true);
        }
    };

    const validateBudgetCodeApi = useMutation((payload) => axios.post(endPoints.ssp.vm.budgetCode, payload), {
        onSuccess: (data) => {
            const isValid = Boolean(data?.data?.valid);
            handleBudgetCodeResult(isValid);
        },
        onError: (error) => {
            const msg = (Array.isArray(error) ? error[0] : undefined) || 'Budget Code is not valid';
            handleBudgetCodeResult(false, msg);
        },
    });

    const handleValidate = () => {
        const trimmedVal = order?.budgetCode?.trim();
        if (trimmedVal && !hPrefixError) {
            validateBudgetCodeApi.mutate({ budgetCode: trimmedVal });
        }
    };

    const apiValidationError = Boolean(apiValidationMessage);
    const isNotValidated = Boolean(order?.budgetCode && !budgetCodeValid && !hPrefixError && order?.enabledManualBudgetCode !== true);

    const validateInput = () => {
        if ((isTouched && order?.isTouched && !order?.budgetCode) || hPrefixError || isNotValidated) {
            return 'validate';
        }
        return '';
    };

    React.useEffect(() => {
        if (!order?.budgetCode && order?.isTouched) setIsTouched(true);
        else setIsTouched(false);
    }, [order?.budgetCode, order?.isTouched]);

    return (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={ProvisioningStyles.stack} data-testid="h-code-box">
            <CssTextField
                key="costCenter"
                size="small"
                sx={{
                    width: (theme) => theme.spacing('100%'),
                    '& .MuiFormHelperText-root': {
                        marginTop: (theme) => theme.spacing('-2'),
                    },
                }}
                autoComplete="off"
                label={<InputLabel label="Cost Center, GL Account or PEP Element" isRequired />}
                error={hPrefixError || apiValidationError || isNotValidated}
                helperText={hPrefixError ? COST_CENTER_H_PREFIX_HELPER_TEXT : apiValidationMessage || (isNotValidated ? COST_CENTER_VALIDATE_HELPER_TEXT : '')}
                value={order?.budgetCode || ''}
                onChange={(e) => setBudgetCode(e.target.value)}
                className={validateInput()}
            />
            <Btn
                className="btn-disabled custom-btn"
                onClick={handleValidate}
                disabled={!order?.budgetCode || hPrefixError || validateBudgetCodeApi.isLoading}
                size="small"
                sx={ProvisioningStyles.dataDisk.configureBtn}
                variant="outlined"
                data-testid="validate-btn"
            >
                Validate*
            </Btn>
            <HelpImg title={COST_CENTER_H_TOOLTIP_TEXT} />
        </Stack>
    );
};

export default BudgetCode;
